<template>
  <div class="flex flex-col gap-4 rounded-md bg-white">
    <div class="grid grid-cols-3 gap-4">
      <Select
        v-model="selectedCounterpartyType"
        :options="counterpartyTypeOptions"
        :label="t('counterpartyType.label')"
      />
      <Select v-model="selectedPaymentOption" :options="paymentOptions" :label="t('payment')" />
      <Select v-model="selectionMode" :label="t('selection')" :options="selectionModeOptions" />
    </div>
    <RefundPayment
      v-if="selectedPayment"
      :key="`${selectedPayment.id}-${selectionMode}`"
      :booking-id="booking.id"
      :payment="selectedPayment"
      :selection="selectionMode"
      @success="$emit('success')"
      @cancel="$emit('cancel')"
    />
  </div>
</template>

<script lang="ts" setup>
import type { SelectOption } from '@/components/Select.vue';
import Select from '@/components/Select.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import type { Payment } from '@/entities/payments/payment.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import { formatCurrency } from '@/utils/format-numbers';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RefundPayment from '../ledger-entries/RefundPayment.vue';
import { PaymentStatus } from '@carvia/ros-client-types';

type AllocationMode = 'all' | 'manual';

const props = defineProps<{
  booking: Booking;
  payments: Payment[];
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'success'): void;
}>();

const { t, locale } = useI18n();

const selectedCounterpartyType = ref<CounterpartyType>(CounterpartyType.CUSTOMER);

const selectionMode = ref<AllocationMode>('all');

const selectionModeOptions = computed<SelectOption[]>(() => [
  { label: t('selectAll'), value: 'all' },
  { label: t('selectManual'), value: 'manual' },
]);

const selectedPaymentOption = ref<string | undefined>();
const paymentOptions = computed<SelectOption[]>(() =>
  props.payments
    .filter(
      ({ counterpartyType, status }) =>
        (!counterpartyType || counterpartyType === selectedCounterpartyType.value) &&
        status === PaymentStatus.PAID,
    )
    .map((payment) => ({
      value: payment.id,
      label: `${formatDate(payment.date, DateTime.DATE_SHORT)} · ${t(`paymentTypes.${payment.paymentType}`)} · ${payment.creditCardDetails ? payment.creditCardDetails?.number?.slice(-3) + ' · ' : ''}${formatCurrency(payment.amount)}${payment.referenceId ? ' · ' + payment.referenceId : ''}`,
    })),
);
const selectedPayment = computed(() =>
  props.payments.find(({ id }) => id === selectedPaymentOption.value),
);

const counterpartyTypeOptions = useCounterpartyTypeOptions();
</script>

<i18n lang="json">
{
  "en": {
    "payment": "Payment",
    "selection": "Selection",
    "selectAll": "All",
    "selectManual": "Select manually",
    "refund": "Refund"
  },
  "de": {
    "payment": "Zahlung",
    "selection": "Auswahl",
    "selectAll": "Alles",
    "selectManual": "Selbst wählen",
    "refund": "Erstattung"
  },
  "es": {
    "payment": "Pago",
    "selection": "Selección",
    "selectAll": "Todo",
    "selectManual": "Seleccionar manualmente",
    "refund": "Reembolsar"
  }
}
</i18n>
